// MOST LIKELY — "most likely to ___ in this situation" (Haley, 2026-10-12:
// "I would like the overarching patterns/habits of the character to be
// cumulative" / "Like most likely to blank in this situation").
//
// scripts/emotion_measure.py measures, for every exchange in every room the
// character was played in (AU included), what the other writer's character did
// and what this character answered with, and stores the cumulative table in
// character_emotion_profiles.profile.situations (and .situations_with[partner]
// for the people they share the most scenes with). This module reads the LIVE
// scene with the same families (src/situations.json) so the director can say,
// at depth 0, what the archive's odds are for exactly this moment.
import SITUATIONS from './situations.json';

const SITUATION_RX = Object.entries(SITUATIONS.situations).map(([key, v]) => [key, v.label, new RegExp(v.re, 'i')]);

/** Situation keys present in a message (the partner's), in the file's order. */
export function situationsIn(text) {
  const t = String(text || '');
  if (!t.trim()) return [];
  return SITUATION_RX.filter(([, , rx]) => rx.test(t)).map(([key]) => key);
}

export function situationLabel(key) {
  return SITUATIONS.situations[key]?.label || key;
}

/** The measured table for this partner when one exists with enough exchanges, else the cumulative one. */
export function tableFor(emotion, partnerName = '') {
  if (!emotion || typeof emotion !== 'object') return null;
  const want = String(partnerName || '').trim().toLowerCase();
  const withT = emotion.situations_with && typeof emotion.situations_with === 'object' ? emotion.situations_with : null;
  if (want && withT) {
    const key = Object.keys(withT).find(k => String(k).toLowerCase() === want);
    if (key && Array.isArray(withT[key]?.table) && withT[key].table.length) return { scope: key, ...withT[key] };
  }
  const all = emotion.situations;
  if (all && Array.isArray(all.table) && all.table.length) return { scope: '', ...all };
  return null;
}

/** Rows for these situation keys: the partner's table first, the cumulative one for whatever it lacks. */
export function rowsFor(emotion, keys, partnerName = '') {
  const pair = tableFor(emotion, partnerName);
  const all = (emotion && emotion.situations && Array.isArray(emotion.situations.table)) ? { scope: '', ...emotion.situations } : null;
  const out = [];
  for (const k of keys) {
    const fromPair = pair && pair.scope ? pair.table.find(r => r.situation === k) : null;
    const fromAll = all ? all.table.find(r => r.situation === k) : null;
    const row = fromPair || fromAll;
    if (row) out.push({ ...row, scope: fromPair ? pair.scope : '' });
  }
  return out;
}

/**
 * One director line for the situation the partner's latest message is in.
 * Per-partner odds where they exist, the cumulative odds otherwise. '' when the
 * message matches nothing measured, so the line never fires on guesswork.
 */
export function mostLikelyLine(lastPlayerText, emotion, { who = 'you', partner = 'they', plain = false } = {}) {
  const keys = situationsIn(lastPlayerText);
  if (!keys.length) return '';
  const rows = rowsFor(emotion, keys, partner).slice(0, 2);
  if (!rows.length) return '';
  const parts = rows.map(r => {
    const odds = (r.responses || []).slice(0, 3).map(x => `${x.behavior} (${x.pct}% of ${r.n}${x.base_pct != null ? `, usually ${x.base_pct}%` : ''})`).join(', ');
    const scope = r.scope ? `with ${r.scope} in particular` : 'cumulative across every room';
    return `${r.label} (${scope}): ${odds}`;
  });
  return plain
    ? `Right now, measured: ${parts.join('; ')}. Reach for the top of that list first; only what this story has already established moves ${who} off it.`
    : `RIGHT NOW, MEASURED — ${parts.join('; ')}. Reach for the top of that list first; only what this story has already established moves ${who} off it.`;
}
